import React, { FC, useCallback, useState } from 'react';
import { Alert, Button, Form, Input, Modal } from 'antd';
import OWButton from '../ow_button/OWButton';
import { getDisplayableErrorMessage } from '../../utils/DataAccessUtils';

const FORM_ID = 'cancel-pr-line-item-form';

interface CancelPRLineItemModalProps {
	lineItemName: string;
	onCancel: () => void;
	onConfirm: (cancelReason: string) => Promise<any>;
}

export const CancelPRLineItemModal: FC<CancelPRLineItemModalProps> = ({
	lineItemName,
	onCancel,
	onConfirm,
}) => {
	const [form] = Form.useForm();
	const [cancelling, setCancelling] = useState(false);
	const [cancelError, setCancelError] = useState(null);

	const onSubmit = useCallback(
		(values) => {
			setCancelling(true);
			setCancelError(null);
			onConfirm(values.cancelReason.trim()).catch((error) => {
				setCancelError(getDisplayableErrorMessage(error, 'Unable to cancel this item.'));
				setCancelling(false);
			});
		},
		[onConfirm]
	);

	return (
		<Modal
			visible
			title={lineItemName ? `Cancel ${lineItemName}?` : 'Cancel this item?'}
			// No dismissal while the request is in flight: the parent clears its selection on
			// success, which would close a modal opened for another item in the meantime.
			closable={!cancelling}
			maskClosable={!cancelling}
			keyboard={!cancelling}
			onCancel={onCancel}
			footer={[
				<Button key="keep" onClick={onCancel} disabled={cancelling}>
					Keep
				</Button>,
				<OWButton
					key="submit"
					danger
					type="primary"
					htmlType="submit"
					form={FORM_ID}
					loading={cancelling}
				>
					Cancel item
				</OWButton>,
			]}
		>
			<Form form={form} id={FORM_ID} layout="vertical" requiredMark={false} onFinish={onSubmit}>
				<Form.Item
					name="cancelReason"
					className="!mb-0"
					rules={[
						{ required: true, whitespace: true, message: 'Please enter a cancellation reason' },
					]}
				>
					<Input.TextArea rows={3} maxLength={1000} placeholder="Cancellation reason" autoFocus />
				</Form.Item>
				{cancelError && <Alert className="mt-s" type="error" message={cancelError} />}
			</Form>
		</Modal>
	);
};
